<template>
  <section class="priority-page" :dir="pageDirection">
    <!-- Header -->
    <div class="header">
      <h1>{{ t("priorityEngine.title") }}</h1>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-wrapper">
      <div class="loading-card">
        <v-progress-circular
          indeterminate
          size="70"
          width="6"
          color="primary"
        />

        <h3>{{ t("priorityEngine.loading") }}</h3>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="errorMessage" class="error-box">
      {{ errorMessage }}
    </div>

    <!-- Content -->
    <template v-else>
      <!-- Summary cards -->

      <!-- Comprehensive FIS table -->
      <div class="table-card comprehensive-card">
        <div class="comprehensive-heading">
          <div class="comprehensive-title-block">
            <h2>{{ t("priorityEngine.comprehensive.title") }}</h2>
            <p>{{ t("priorityEngine.comprehensive.description") }}</p>
          </div>

          <div class="facility-count-card">
            <span>{{ t("priorityEngine.comprehensive.facilitiesCount") }}</span>
            <strong>{{ comprehensiveFis.length }}</strong>
          </div>
        </div>

        <div class="table-wrap comprehensive-wrap">
          <table class="comprehensive-table">
            <thead>
              <tr>
                <th class="fis-rank-column">#</th>
                <th class="fis-facility-column">
                  {{ t("priorityEngine.table.facility") }}
                </th>

                <th>
                  <div class="header-score">
                    <span>{{
                      t("priorityEngine.comprehensive.infrastructure")
                    }}</span>
                    <small>25%</small>
                  </div>
                </th>

                <th>
                  <div class="header-score">
                    <span>{{
                      t("priorityEngine.comprehensive.services")
                    }}</span>
                    <small>30%</small>
                  </div>
                </th>

                <th>
                  <div class="header-score">
                    <span>{{
                      t("priorityEngine.comprehensive.workforce")
                    }}</span>
                    <small>20%</small>
                  </div>
                </th>

                <th>
                  <div class="header-score">
                    <span>{{ t("priorityEngine.comprehensive.finance") }}</span>
                    <small>15%</small>
                  </div>
                </th>

                <th>
                  <div class="header-score">
                    <span>{{
                      t("priorityEngine.comprehensive.governance")
                    }}</span>
                    <small>10%</small>
                  </div>
                </th>

                <th class="fis-result-header">
                  {{ t("priorityEngine.comprehensive.finalFis") }}
                </th>
                <th class="comprehensive-priority-header">
                  {{ t("priorityEngine.table.priorityClass") }}
                </th>

                <th class="comprehensive-timeframe-header">
                  {{ t("priorityEngine.table.timeframe") }}
                </th>

                <th class="comprehensive-response-header">
                  {{ t("priorityEngine.table.responseRequired") }}
                </th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="(item, index) in comprehensiveFis"
                :key="`fis-${item.survey_id}`"
              >
                <td class="rank" data-label="#">{{ index + 1 }}</td>

                <td
                  class="name"
                  :data-label="t('priorityEngine.table.facility')"
                >
                  {{ item.facility_name || "-" }}
                </td>

                <td
                  :data-label="t('priorityEngine.comprehensive.infrastructure')"
                >
                  <div class="fis-score-cell">
                    <strong>{{ formatNumber(item.infra) }}</strong>
                    <small>
                      {{ formatNumber(item.infra_100) }}
                      {{ t("priorityEngine.comprehensive.outOf100") }}
                    </small>
                  </div>
                </td>

                <td :data-label="t('priorityEngine.comprehensive.services')">
                  <div class="fis-score-cell">
                    <strong>{{ formatNumber(item.services) }}</strong>
                    <small>
                      {{ formatNumber(item.services_100) }}
                      {{ t("priorityEngine.comprehensive.outOf100") }}
                    </small>
                  </div>
                </td>

                <td :data-label="t('priorityEngine.comprehensive.workforce')">
                  <div class="fis-score-cell">
                    <strong>{{ formatNumber(item.staff) }}</strong>
                    <small>
                      {{ formatNumber(item.staff_100) }}
                      {{ t("priorityEngine.comprehensive.outOf100") }}
                    </small>
                  </div>
                </td>

                <td :data-label="t('priorityEngine.comprehensive.finance')">
                  <div class="fis-score-cell">
                    <strong>{{ formatNumber(item.fin) }}</strong>
                    <small>
                      {{ formatNumber(item.fin_100) }}
                      {{ t("priorityEngine.comprehensive.outOf100") }}
                    </small>
                  </div>
                </td>

                <td :data-label="t('priorityEngine.comprehensive.governance')">
                  <div class="fis-score-cell">
                    <strong>{{ formatNumber(item.gov_avg) }}</strong>
                    <small>
                      {{ formatNumber(item.gov_avg_100) }}
                      {{ t("priorityEngine.comprehensive.outOf100") }}
                    </small>
                  </div>
                </td>

                <td
                  class="fis-result-cell"
                  :data-label="t('priorityEngine.comprehensive.finalFis')"
                >
                  {{ formatNumber(item.fis_result ?? item.FIS_result) }}
                </td>

                <td
                  class="comprehensive-priority-cell"
                  :data-label="t('priorityEngine.table.priorityClass')"
                >
                  <template v-if="item.fis_priority_code">
                    <span class="priority-pill" :class="item.fis_priority_code">
                      {{ item.fis_priority_code }}
                    </span>

                    <div class="priority-name">
                      {{ cleanPriorityCategory(item.fis_priority) }}
                    </div>
                  </template>

                  <span v-else>-</span>
                </td>

                <td
                  class="comprehensive-timeframe-cell"
                  :data-label="t('priorityEngine.table.timeframe')"
                >
                  {{ item.fis_timeframe || "-" }}
                </td>

                <td
                  class="comprehensive-response-cell"
                  :data-label="t('priorityEngine.table.responseRequired')"
                >
                  {{ item.fis_response_required || "-" }}
                </td>
              </tr>

              <tr v-if="comprehensiveFis.length === 0">
                <td colspan="11" class="empty-row">
                  {{ t("priorityEngine.comprehensive.empty") }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Priority table -->
      <div class="table-card">
        <div class="table-header">
          <div>
            <h2>{{ t("priorityEngine.table.title") }}</h2>
            <p>{{ t("priorityEngine.description") }}</p>
          </div>

          <div class="legend">
            <span class="legend-item">
              <i class="legend-color p1"></i>
              P1 {{ t("priorityEngine.priorities.P1") }}
            </span>

            <span class="legend-item">
              <i class="legend-color p2"></i>
              P2 {{ t("priorityEngine.priorities.P2") }}
            </span>

            <span class="legend-item">
              <i class="legend-color p3"></i>
              P3 {{ t("priorityEngine.priorities.P3") }}
            </span>

            <span class="legend-item">
              <i class="legend-color p4"></i>
              P4 {{ t("priorityEngine.priorities.P4") }}
            </span>
          </div>
        </div>

        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th class="rank-column">#</th>
                <th class="facility-column">
                  {{ t("priorityEngine.table.facility") }}
                </th>
                <th>{{ t("priorityEngine.table.type") }}</th>

                <th>
                  <div class="header-score">
                    <span>{{ t("priorityEngine.table.Damage") }}</span>
                    <small>35%</small>
                  </div>
                </th>

                <th>
                  <div class="header-score">
                    <span>{{ t("priorityEngine.table.serviceVolume") }}</span>
                    <small>30%</small>
                  </div>
                </th>

                <th>
                  <div class="header-score">
                    <span>{{ t("priorityEngine.table.population") }}</span>
                    <small>20%</small>
                  </div>
                </th>

                <th>
                  <div class="header-score">
                    <span>{{ t("priorityEngine.table.feasibility") }}</span>
                    <small>10%</small>
                  </div>
                </th>

                <th>
                  <div class="header-score">
                    <span>{{ t("priorityEngine.table.equity") }}</span>
                    <small>5%</small>
                  </div>
                </th>

                <th>{{ t("priorityEngine.table.priorityScore") }}</th>
                <th>{{ t("priorityEngine.table.priorityClass") }}</th>
                <th>{{ t("priorityEngine.table.timeframe") }}</th>
                <th>{{ t("priorityEngine.table.responseRequired") }}</th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="item in priorities"
                :key="item.survey_id"
                :class="getRowClass(item.priority_code)"
              >
                <td class="rank" data-label="#">{{ item.rank }}</td>

                <td
                  class="name"
                  :data-label="t('priorityEngine.table.facility')"
                >
                  {{ item.facility_name }}
                </td>

                <td
                  class="facility-type"
                  :data-label="t('priorityEngine.table.type')"
                >
                  {{ item.facility_type || "-" }}
                </td>

                <td :data-label="t('priorityEngine.table.severity')">
                  <div class="score-cell">
                    <strong>{{ formatNumber(item.fis_35) }}</strong>
                    <small>{{ t("priorityEngine.common.of") }} 35</small>
                  </div>
                </td>

                <td :data-label="t('priorityEngine.table.serviceVolume')">
                  <div class="score-cell">
                    <strong>{{ formatNumber(item.srv_30) }}</strong>
                    <small>{{ t("priorityEngine.common.of") }} 30</small>
                  </div>
                </td>

                <td :data-label="t('priorityEngine.table.population')">
                  <div class="score-cell">
                    <strong>{{ formatNumber(item.population_20) }}</strong>
                    <small>{{ t("priorityEngine.common.of") }} 20</small>
                  </div>
                </td>

                <td :data-label="t('priorityEngine.table.feasibility')">
                  <div class="score-cell">
                    <strong>{{ formatNumber(item.feas_10) }}</strong>
                    <small>{{ t("priorityEngine.common.of") }} 10</small>
                  </div>
                </td>

                <td :data-label="t('priorityEngine.table.equity')">
                  <div class="score-cell">
                    <strong>{{ formatNumber(item.eq_gender_5) }}</strong>
                    <small>{{ t("priorityEngine.common.of") }} 5</small>
                  </div>
                </td>

                <td
                  class="priority-score"
                  :data-label="t('priorityEngine.table.priorityScore')"
                >
                  {{ formatNumber(item.priority_score) }}
                </td>

                <td :data-label="t('priorityEngine.table.priorityClass')">
                  <span class="priority-pill" :class="getPriorityCode(item)">
                    {{ getPriorityCode(item) }}
                  </span>
                  <div class="priority-name">
                    {{ cleanPriorityCategory(item.priority_category) }}
                  </div>
                </td>

                <td
                  class="timeframe"
                  :data-label="t('priorityEngine.table.timeframe')"
                >
                  {{ item.timeframe || "-" }}
                </td>

                <td
                  class="response-required"
                  :data-label="t('priorityEngine.table.responseRequired')"
                >
                  {{ item.response_required || "-" }}
                </td>
              </tr>

              <tr v-if="priorities.length === 0">
                <td colspan="12" class="empty-row">
                  {{ t("priorityEngine.empty") }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </section>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import api from "@/plugins/api";

const { t, locale } = useI18n();
const pageDirection = computed(() => (locale.value === "ar" ? "rtl" : "ltr"));

const loading = ref(true);
const errorMessage = ref("");
const facilities = ref([]);
const comprehensiveFis = ref([]);

/**
 * تنسيق الأرقام إلى منزلة عشرية واحدة.
 */
const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const number = Number(value);

  if (!Number.isFinite(number)) {
    return "-";
  }

  return number.toFixed(1);
};

/**
 * تحديد كود الأولوية.
 *
 * يستخدم priority_code القادم من API أولاً.
 * وإذا لم يكن موجوداً، يستخرجه من التصنيف النصي.
 */
const getPriorityCode = (item) => {
  const code = String(item?.priority_code || "")
    .trim()
    .toUpperCase();

  if (["P1", "P2", "P3", "P4"].includes(code)) {
    return code;
  }

  const category = String(item?.priority_category || "");

  if (category.includes("الأولى") || category.includes("حرجة")) {
    return "P1";
  }

  if (category.includes("الثانية") || category.includes("عالية")) {
    return "P2";
  }

  if (category.includes("الثالثة") || category.includes("متوسطة")) {
    return "P3";
  }

  return "P4";
};

/**
 * إزالة النص الإنجليزي من تصنيف الأولوية.
 *
 * مثال:
 * "متوسطة | Medium" تصبح "متوسطة".
 */
const cleanPriorityCategory = (category) => {
  if (!category) {
    return "-";
  }

  const parts = String(category)
    .split("|")
    .map((part) => part.trim())
    .filter(Boolean);

  if (locale.value === "en" && parts.length > 1) {
    return parts[1];
  }

  return parts[0] || "-";
};

/**
 * لون خفيف للصف حسب الأولوية.
 */
const getRowClass = (priorityCode) => {
  const code = String(priorityCode || "")
    .trim()
    .toUpperCase();

  return {
    "priority-row-p1": code === "P1",
    "priority-row-p2": code === "P2",
    "priority-row-p3": code === "P3",
    "priority-row-p4": code === "P4",
  };
};

/**
 * ترتيب البيانات حسب priority_score وليس FIS.
 */
const toNullableNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const number = Number(value);

  return Number.isFinite(number) ? number : null;
};

const priorities = computed(() => {
  return facilities.value
    .map((item) => ({
      ...item,
      fis_35: toNullableNumber(item.fis_35),
      srv_30: toNullableNumber(item.srv_30),
      population_20: toNullableNumber(item.population_20),
      feas_10: toNullableNumber(item.feas_10),
      eq_gender_5: toNullableNumber(item.eq_gender_5),
      priority_score: toNullableNumber(item.priority_score),
    }))
    .sort((a, b) => {
      const scoreA = a.priority_score ?? -Infinity;
      const scoreB = b.priority_score ?? -Infinity;

      if (scoreB !== scoreA) {
        return scoreB - scoreA;
      }

      return Number(a.survey_id) - Number(b.survey_id);
    })
    .map((item, index) => ({
      ...item,
      rank: index + 1,
    }));
});

/**
 * ملخص عدد المرافق في كل تصنيف.
 */

/**
 * تحميل نتيجة FIS المتكاملة.
 */
const loadComprehensiveFIS = async () => {
  try {
    const response = await api.get("/fis-comprehensive");

    comprehensiveFis.value = Array.isArray(response.data?.data)
      ? response.data.data
      : [];
  } catch (error) {
    console.error("Comprehensive FIS API error:", error);
    comprehensiveFis.value = [];
  }
};

/**
 * تحميل بيانات الأولويات.
 */
const loadPriorities = async () => {
  loading.value = true;
  errorMessage.value = "";

  try {
    const response = await api.get("/rehab-priorities");

    facilities.value = Array.isArray(response.data?.data)
      ? response.data.data
      : [];
  } catch (error) {
    console.error("Priority API error:", error);

    errorMessage.value =
      error?.response?.data?.message || t("priorityEngine.loadError");
  } finally {
    loading.value = false;
  }
};

onMounted(async () => {
  await Promise.all([loadComprehensiveFIS(), loadPriorities()]);
});
</script>

<style scoped>
.priority-page {
  width: 100%;

  min-height: 100vh;

  padding: 80px 24px 60px;

  background: #f4f6f9;

  color: #061226;

  font-family: Cairo, Arial, sans-serif;

  box-sizing: border-box;
}

.priority-page *,
.priority-page *::before,
.priority-page *::after {
  box-sizing: border-box;
}

/* ================= Header ================= */

.header {
  width: 100%;

  margin: 0 0 30px;

  text-align: right;
}

.header h1 {
  margin: 0 0 10px;

  color: #071126;

  font-size: 28px;

  font-weight: 900;
}

.header p {
  width: 100%;

  max-width: 1250px;

  margin: 0;

  color: #334155;

  font-size: 14px;

  line-height: 2;
}

.header span {
  display: inline-block;

  margin-top: 12px;

  padding: 7px 16px;

  border: 1px solid #dbe3ee;

  border-radius: 999px;

  background: #fff;

  color: #64748b;

  font-size: 12px;
}

/* ================= Summary ================= */

.summary-grid {
  display: grid;

  width: 100%;

  grid-template-columns: repeat(5, minmax(0, 1fr));

  gap: 16px;

  margin: 0 0 24px;
}

.summary-card {
  position: relative;

  overflow: hidden;

  min-width: 0;

  padding: 20px;

  border: 1px solid #dbe3ee;

  border-radius: 14px;

  background: #fff;

  box-shadow: 0 5px 18px rgba(15, 23, 42, 0.04);
}

.summary-card::before {
  position: absolute;

  top: 0;

  right: 0;

  width: 5px;

  height: 100%;

  background: #143a63;

  content: "";
}

.summary-card span {
  display: block;

  color: #64748b;

  font-size: 13px;

  font-weight: 700;
}

.summary-card strong {
  display: block;

  margin-top: 8px;

  color: #071126;

  font-size: 28px;

  font-weight: 900;
}

.p1-card::before {
  background: #b91c1c;
}

.p2-card::before {
  background: #e87900;
}

.p3-card::before {
  background: #2563eb;
}

.p4-card::before {
  background: #15803d;
}

/* ================= Table card ================= */

.table-card {
  width: 100%;

  margin: 0;

  padding: 24px 18px 28px;

  border: 1px solid #dbe3ee;

  border-radius: 14px;

  background: #fff;
}

.table-header {
  display: flex;

  align-items: flex-start;

  justify-content: space-between;

  gap: 20px;

  margin-bottom: 20px;
}

.table-header h2 {
  margin: 0;

  color: #071126;

  font-size: 21px;

  font-weight: 900;
}

.table-header p {
  margin: 6px 0 0;

  color: #64748b;

  font-size: 13px;
}

.legend {
  display: flex;

  flex-wrap: wrap;

  gap: 12px;
}

.legend-item {
  display: inline-flex;

  align-items: center;

  gap: 6px;

  color: #475569;

  font-size: 12px;

  font-weight: 700;
}

.legend-color {
  width: 11px;

  height: 11px;

  flex: 0 0 11px;

  border-radius: 50%;
}

.legend-color.p1 {
  background: #b91c1c;
}

.legend-color.p2 {
  background: #e87900;
}

.legend-color.p3 {
  background: #2563eb;
}

.legend-color.p4 {
  background: #15803d;
}

/* ================= Table ================= */

.table-wrap {
  width: 100%;

  max-width: 100%;

  overflow: hidden;

  border-radius: 11px;
}

table {
  width: 100%;

  max-width: 100%;

  table-layout: fixed;

  min-width: 0;

  border-spacing: 0;

  border-collapse: collapse;
}

thead {
  background: #143a63;
}

th,
td {
  min-width: 0;

  padding: 14px 7px;

  text-align: center;

  vertical-align: middle;

  white-space: normal;

  overflow-wrap: anywhere;

  word-break: normal;
}

th {
  color: #fff;

  font-size: 12px;

  font-weight: 900;

  line-height: 1.5;
}

td {
  border-bottom: 1px solid #dbe3ee;

  color: #071126;

  font-size: 12px;

  line-height: 1.6;
}

th:first-child {
  border-radius: 0 10px 0 0;
}

th:last-child {
  border-radius: 10px 0 0 0;
}

/* عرض الأعمدة */

th:nth-child(1),
td:nth-child(1) {
  width: 4%;
}

th:nth-child(2),
td:nth-child(2) {
  width: 14%;
}

th:nth-child(3),
td:nth-child(3) {
  width: 9%;
}

th:nth-child(4),
td:nth-child(4),
th:nth-child(5),
td:nth-child(5),
th:nth-child(6),
td:nth-child(6),
th:nth-child(7),
td:nth-child(7),
th:nth-child(8),
td:nth-child(8) {
  width: 6%;
}

th:nth-child(9),
td:nth-child(9) {
  width: 7%;
}

th:nth-child(10),
td:nth-child(10) {
  width: 8%;
}

th:nth-child(11),
td:nth-child(11) {
  width: 9%;
}

th:nth-child(12),
td:nth-child(12) {
  width: 14%;
}

.rank-column,
.facility-column,
.timeframe,
.response-required {
  min-width: 0;
}

.header-score {
  display: flex;

  flex-direction: column;

  align-items: center;

  gap: 2px;
}

.header-score small {
  color: #dbeafe;

  font-size: 10px;
}

tbody tr {
  transition: background-color 0.2s ease;
}

tbody tr:hover {
  background: #f8fafc;
}

.rank {
  color: #0f2340;

  font-size: 15px;

  font-weight: 900;
}

.name {
  color: #071126;

  font-size: 13px;

  font-weight: 900;

  line-height: 1.7;

  text-align: right;
}

.facility-type {
  color: #334155;

  line-height: 1.7;
}

.score-cell {
  display: flex;

  flex-direction: column;

  align-items: center;

  gap: 3px;
}

.score-cell strong {
  color: #0f2340;

  font-size: 14px;

  font-weight: 900;
}

.score-cell small {
  color: #94a3b8;

  font-size: 9px;

  line-height: 1.4;
}

.priority-score {
  color: #071126;

  font-size: 17px;

  font-weight: 900;
}

.priority-pill {
  display: inline-flex;

  align-items: center;

  justify-content: center;

  min-width: 48px;

  min-height: 29px;

  padding: 5px 10px;

  border-radius: 999px;

  color: #fff;

  font-size: 11px;

  font-weight: 900;
}

.priority-pill.P1 {
  background: #b91c1c;
}

.priority-pill.P2 {
  background: #e87900;
}

.priority-pill.P3 {
  background: #2563eb;
}

.priority-pill.P4 {
  background: #15803d;
}

.priority-name {
  margin-top: 6px;

  color: #64748b;

  font-size: 10px;

  font-weight: 700;
}

.timeframe {
  line-height: 1.7;
}

.response-required {
  color: #334155;

  line-height: 1.7;

  text-align: right;
}

/* ================= Row indicators ================= */

.priority-row-p1 td:first-child {
  border-right: 4px solid #b91c1c;
}

.priority-row-p2 td:first-child {
  border-right: 4px solid #e87900;
}

.priority-row-p3 td:first-child {
  border-right: 4px solid #2563eb;
}

.priority-row-p4 td:first-child {
  border-right: 4px solid #15803d;
}

/* ================= Loading ================= */

.loading-wrapper {
  position: fixed;

  z-index: 99999;

  inset: 0;

  display: flex;

  align-items: center;

  justify-content: center;

  background: rgba(255, 255, 255, 0.93);
}

.loading-card {
  padding: 34px 28px;

  border-radius: 20px;

  background: #fff;

  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.12);

  text-align: center;
}

.loading-card h3 {
  margin: 18px 0 0;

  font-size: 18px;

  font-weight: 800;
}

/* ================= Error and empty ================= */

.error-box {
  width: 100%;

  max-width: 900px;

  margin: 30px auto;

  padding: 25px;

  border: 1px solid #fecaca;

  border-radius: 12px;

  background: #fef2f2;

  color: #b91c1c;

  font-weight: 900;

  text-align: center;
}

.empty-row {
  padding: 40px !important;

  color: #64748b;

  font-weight: 900;

  text-align: center !important;
}

/* ================= Comprehensive FIS table ================= */

.comprehensive-card {
  margin-bottom: 24px;

  padding: 24px;

  overflow: hidden;

  border: 1px solid #dce5ef;

  border-radius: 18px;

  box-shadow: 0 10px 30px rgba(15, 35, 64, 0.06);
}

.comprehensive-heading {
  display: flex;

  align-items: center;

  justify-content: space-between;

  gap: 24px;

  margin-bottom: 22px;
}

.comprehensive-title-block h2 {
  margin: 0;

  color: #071126;

  font-size: 23px;

  font-weight: 900;
}

.comprehensive-title-block p {
  max-width: 900px;

  margin: 8px 0 0;

  color: #64748b;

  font-size: 13px;

  line-height: 1.9;
}

.facility-count-card {
  display: flex;

  width: 150px;

  min-width: 150px;

  min-height: 86px;

  flex-direction: column;

  align-items: center;

  justify-content: center;

  padding: 14px;

  border: 1px solid #dbe5f0;

  border-radius: 14px;

  background: #f8fbff;

  box-shadow: 0 5px 16px rgba(15, 35, 64, 0.05);
}

.facility-count-card span {
  color: #64748b;

  font-size: 12px;

  font-weight: 700;
}

.facility-count-card strong {
  margin-top: 4px;

  color: #143a63;

  font-size: 26px;

  font-weight: 900;

  line-height: 1;
}

.comprehensive-wrap {
  overflow-x: auto;

  border: 1px solid #dbe5ef;

  border-radius: 14px;

  background: #fff;
}

.comprehensive-table {
  width: 100%;

  min-width: 980px;

  table-layout: fixed;

  border-collapse: separate;

  border-spacing: 0;
}

.comprehensive-table thead {
  background: linear-gradient(180deg, #173f6b 0%, #12365c 100%);
}

.comprehensive-table th,
.comprehensive-table td {
  width: auto !important;

  min-width: 0;

  padding: 18px 12px;
}

.comprehensive-table th {
  height: 112px;

  border-inline-start: 1px solid rgba(255, 255, 255, 0.16);

  color: #fff;

  font-size: 13px;

  vertical-align: middle;
}

.comprehensive-table th:first-child {
  border-inline-start: 0;
}

.comprehensive-table .header-score {
  gap: 7px;
}

.comprehensive-table .header-score span {
  font-size: 14px;

  font-weight: 900;
}

.comprehensive-table .header-score::after {
  color: #dbeafe;

  font-size: 11px;

  font-weight: 700;

  content: "/100";
}

.comprehensive-table .header-score small {
  display: inline-flex;

  min-width: 46px;

  min-height: 28px;

  align-items: center;

  justify-content: center;

  margin-top: 1px;

  padding: 4px 9px;

  border: 1px solid rgba(255, 255, 255, 0.12);

  border-radius: 9px;

  background: rgba(255, 255, 255, 0.12);

  color: #fff;

  font-size: 11px;

  font-weight: 900;
}

.comprehensive-table tbody tr {
  background: #fff;

  transition: background-color 0.2s ease, transform 0.2s ease;
}

.comprehensive-table tbody tr:nth-child(even) {
  background: #fcfdff;
}

.comprehensive-table tbody tr:hover {
  background: #f7fbff;
}

.comprehensive-table tbody td {
  height: 108px;

  border-inline-start: 1px solid #e8eef5;

  border-bottom: 1px solid #e2e8f0;
}

.comprehensive-table tbody tr:last-child td {
  border-bottom: 0;
}

.comprehensive-table tbody td:first-child {
  border-inline-start: 0;
}

.comprehensive-table .fis-rank-column {
  width: 5% !important;
}

.comprehensive-table .fis-facility-column {
  width: 23% !important;
}

.comprehensive-table th:nth-child(n + 3):nth-child(-n + 7) {
  width: 11% !important;
}

.comprehensive-table .fis-result-header {
  width: 10% !important;

  font-size: 15px;

  font-weight: 900;
}

.comprehensive-table .name {
  color: #0f172a;

  font-size: 14px;

  font-weight: 900;
}

.fis-score-cell {
  display: flex;

  min-width: 0;

  flex-direction: column;

  align-items: center;

  justify-content: center;

  gap: 8px;
}

.fis-score-cell strong {
  color: #143a63;

  font-size: 22px;

  font-weight: 950;

  line-height: 1;
}

.fis-score-cell small {
  display: inline-flex;

  align-items: center;

  justify-content: center;

  color: #64748b;

  font-size: 10px;

  font-weight: 700;

  line-height: 1.4;

  white-space: nowrap;
}

.fis-result-cell {
  position: relative;

  color: #0f9f3f;

  font-size: 25px !important;

  font-weight: 950;
}

.fis-result-cell::before {
  display: none;
}

/* ================= Responsive ================= */

@media (max-width: 1200px) {
  .comprehensive-heading {
    align-items: flex-start;
  }

  .priority-page {
    padding: 40px 14px 36px;
  }

  .table-header {
    flex-direction: column;
  }

  .legend {
    width: 100%;
  }
}

@media (max-width: 800px) {
  .comprehensive-card {
    padding: 16px 12px;
  }

  .comprehensive-heading {
    flex-direction: column;

    gap: 14px;
  }

  .facility-count-card {
    width: 100%;

    min-width: 0;

    min-height: 68px;

    flex-direction: row;

    gap: 12px;
  }

  .comprehensive-title-block h2 {
    font-size: 18px;
  }

  .comprehensive-title-block p {
    font-size: 12px;
  }

  .priority-page {
    padding: 18px 10px 30px;

    overflow-x: hidden;
  }

  .header {
    margin-bottom: 18px;

    text-align: start;
  }

  .header h1 {
    font-size: 20px;

    line-height: 1.6;
  }

  .header p {
    font-size: 12px;

    line-height: 1.9;
  }

  .header span {
    width: 100%;

    padding: 8px 10px;

    border-radius: 10px;

    line-height: 1.7;
  }

  .table-card {
    padding: 14px 10px 18px;

    border-radius: 14px;
  }

  .table-header {
    gap: 12px;

    margin-bottom: 14px;
  }

  .table-header h2 {
    font-size: 17px;

    line-height: 1.6;
  }

  .table-header p {
    font-size: 12px;

    line-height: 1.7;
  }

  .legend {
    gap: 8px 12px;
  }

  .legend-item {
    font-size: 11px;
  }

  .table-wrap {
    width: 100%;

    overflow: visible;

    border-radius: 0;
  }

  table {
    display: block;

    width: 100%;

    min-width: 0;

    max-width: none;

    table-layout: auto;
  }

  thead {
    display: none;
  }

  tbody {
    display: grid;

    width: 100%;

    gap: 14px;
  }

  tbody tr {
    position: relative;

    display: grid;

    width: 100%;

    min-width: 0;

    padding: 58px 14px 12px;

    overflow: hidden;

    border: 1px solid #dbe3ee;

    border-inline-end: 5px solid #2563eb;

    border-radius: 18px;

    background: #fff;

    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.07);
  }

  tbody tr:hover {
    background: #fff;
  }

  tbody td,
  tbody td:nth-child(1),
  tbody td:nth-child(2),
  tbody td:nth-child(3),
  tbody td:nth-child(4),
  tbody td:nth-child(5),
  tbody td:nth-child(6),
  tbody td:nth-child(7),
  tbody td:nth-child(8),
  tbody td:nth-child(9),
  tbody td:nth-child(10),
  tbody td:nth-child(11),
  tbody td:nth-child(12) {
    display: grid;

    grid-template-columns: minmax(105px, 38%) minmax(0, 1fr);

    align-items: start;

    width: 100%;

    min-width: 0;

    max-width: none;

    gap: 12px;

    padding: 10px 0;

    border-bottom: 1px dashed #e2e8f0;

    font-size: 12px;

    line-height: 1.7;

    text-align: end;

    white-space: normal;

    overflow-wrap: break-word;

    word-break: normal;
  }

  tbody td:last-child {
    border-bottom: 0;
  }

  tbody td::before {
    content: attr(data-label);

    display: block;

    min-width: 0;

    color: #64748b;

    font-size: 11px;

    font-weight: 800;

    line-height: 1.6;

    text-align: start;

    white-space: normal;
  }

  tbody td.rank,
  tbody td.rank:nth-child(1) {
    position: absolute;

    top: 14px;

    inset-inline-start: 14px;

    display: flex;

    width: 42px;

    height: 42px;

    min-width: 42px;

    max-width: 42px;

    padding: 0;

    margin: 0;

    align-items: center;

    justify-content: center;

    border: 1px solid #dbeafe;

    border-inline-end: 3px solid #2563eb;

    border-radius: 50%;

    background: #eff6ff;

    color: #0f2340;

    font-size: 15px;

    font-weight: 900;

    line-height: 1;
  }

  tbody td.rank::before {
    display: none;

    content: none;
  }

  tbody td.name,
  tbody td.name:nth-child(2) {
    display: block;

    width: 100%;

    min-width: 0;

    max-width: none;

    padding: 4px 58px 14px 0;

    border-bottom: 1px solid #e2e8f0;

    color: #071126;

    font-size: 16px;

    font-weight: 900;

    line-height: 1.8;

    text-align: start;

    white-space: normal;

    overflow-wrap: break-word;

    word-break: normal;
  }

  .priority-page[dir="ltr"] tbody td.name,
  .priority-page[dir="ltr"] tbody td.name:nth-child(2) {
    padding: 4px 0 14px 58px;
  }

  tbody td.name::before {
    display: none;

    content: none;
  }

  .score-cell {
    min-width: 0;

    align-items: flex-end;

    text-align: end;
  }

  .score-cell strong {
    font-size: 13px;
  }

  .score-cell small {
    display: block;

    font-size: 9px;

    white-space: normal;
  }

  .facility-type,
  .priority-score,
  .timeframe,
  .response-required {
    min-width: 0;

    max-width: none;

    text-align: end;

    white-space: normal;

    overflow-wrap: break-word;

    word-break: normal;
  }

  .priority-score {
    font-size: 15px;
  }

  .priority-pill {
    min-width: 46px;

    min-height: 27px;

    padding: 4px 8px;

    font-size: 10px;
  }

  .priority-name {
    margin-top: 5px;

    font-size: 10px;

    white-space: normal;
  }

  .priority-row-p1 {
    border-inline-end-color: #b91c1c;
  }

  .priority-row-p2 {
    border-inline-end-color: #e87900;
  }

  .priority-row-p3 {
    border-inline-end-color: #2563eb;
  }

  .priority-row-p4 {
    border-inline-end-color: #15803d;
  }

  .priority-row-p1 td:first-child,
  .priority-row-p2 td:first-child,
  .priority-row-p3 td:first-child,
  .priority-row-p4 td:first-child {
    border-right: 0;

    border-left: 0;
  }

  .empty-row,
  tbody td.empty-row {
    position: static;

    display: block;

    width: 100%;

    max-width: none;

    padding: 28px !important;

    border: 0;

    text-align: center !important;
  }

  .empty-row::before {
    display: none;

    content: none;
  }

  .loading-card {
    width: calc(100% - 28px);

    padding: 26px 18px;
  }
}

@media (max-width: 450px) {
  .priority-page {
    padding-inline: 6px;
  }

  .table-card {
    padding-inline: 7px;
  }

  tbody tr {
    padding-inline: 12px;
  }

  tbody td,
  tbody td:nth-child(n) {
    grid-template-columns: minmax(95px, 36%) minmax(0, 1fr);

    gap: 10px;
  }

  tbody td.name,
  tbody td.name:nth-child(2) {
    font-size: 15px;
  }
}

.priority-page[dir="ltr"] {
  text-align: left;
}

.priority-page[dir="ltr"] .header,
.priority-page[dir="ltr"] .name,
.priority-page[dir="ltr"] .response-required {
  text-align: left;
}

.priority-page[dir="ltr"] th:first-child {
  border-radius: 10px 0 0 0;
}

.priority-page[dir="ltr"] th:last-child {
  border-radius: 0 10px 0 0;
}

.priority-page[dir="ltr"] .priority-row-p1 td:first-child {
  border-right: 0;

  border-left: 4px solid #b91c1c;
}

.priority-page[dir="ltr"] .priority-row-p2 td:first-child {
  border-right: 0;

  border-left: 4px solid #e87900;
}

.priority-page[dir="ltr"] .priority-row-p3 td:first-child {
  border-right: 0;

  border-left: 4px solid #2563eb;
}

.priority-page[dir="ltr"] .priority-row-p4 td:first-child {
  border-right: 0;

  border-left: 4px solid #15803d;
}

/* ================= Compact comprehensive FIS table ================= */

.comprehensive-card {
  padding: 18px;
}

.comprehensive-heading {
  margin-bottom: 16px;
}

.comprehensive-title-block h2 {
  font-size: 20px;
}

.facility-count-card {
  width: 124px;

  min-width: 124px;

  min-height: 68px;
}

.comprehensive-table {
  min-width: 760px;
}

.comprehensive-table th,
.comprehensive-table td {
  padding: 12px 8px;
}

.comprehensive-table th {
  height: 82px;
}

.comprehensive-table tbody td {
  height: 76px;
}

.fis-score-cell {
  gap: 5px;
}

.fis-score-cell strong {
  font-size: 18px;
}

.fis-result-cell {
  font-size: 21px !important;
}

@media (max-width: 800px) {
  .comprehensive-card {
    padding: 12px;

    overflow: visible;
  }

  .comprehensive-heading {
    margin-bottom: 12px;
  }

  .facility-count-card {
    min-height: 54px;

    padding: 10px 12px;
  }

  .comprehensive-wrap {
    overflow: visible;

    border: 0;

    background: transparent;
  }

  .comprehensive-table {
    display: block;

    min-width: 0;

    width: 100%;
  }

  .comprehensive-table thead {
    display: none;
  }

  .comprehensive-table tbody {
    display: grid;

    gap: 12px;
  }

  .comprehensive-table tbody tr {
    display: grid;

    grid-template-columns: repeat(2, minmax(0, 1fr));

    gap: 0;

    padding: 12px;

    border: 1px solid #dbe5ef;

    border-radius: 14px;

    background: #fff;

    box-shadow: 0 5px 16px rgba(15, 35, 64, 0.06);
  }

  .comprehensive-table tbody td,
  .comprehensive-table tbody td:nth-child(n) {
    position: static;

    display: flex;

    width: auto !important;

    min-width: 0;

    height: auto;

    padding: 10px 8px;

    flex-direction: column;

    align-items: center;

    justify-content: center;

    gap: 5px;

    border: 0;

    border-bottom: 1px dashed #e2e8f0;

    text-align: center;
  }

  .comprehensive-table tbody td::before {
    display: block;

    content: attr(data-label);

    color: #64748b;

    font-size: 10px;

    font-weight: 800;

    line-height: 1.4;
  }

  .comprehensive-table tbody td.rank {
    display: none;
  }

  .comprehensive-table tbody td.name {
    grid-column: 1 / -1;

    display: block;

    padding: 4px 4px 12px;

    border-bottom: 1px solid #dbe5ef;

    font-size: 15px;

    text-align: center;
  }

  .comprehensive-table tbody td.name::before {
    display: none;
  }

  .comprehensive-table .fis-score-cell {
    gap: 4px;
  }

  .comprehensive-table .fis-score-cell strong {
    font-size: 17px;
  }

  .comprehensive-table .fis-score-cell small {
    font-size: 9px;

    white-space: normal;
  }

  .comprehensive-table .fis-result-cell {
    grid-column: 1 / -1;

    padding-top: 12px;

    border-bottom: 0;

    color: #0f9f3f;

    font-size: 22px !important;
  }

  .comprehensive-table .fis-result-cell::before {
    display: block;

    color: #64748b;

    font-size: 10px;
  }

  .comprehensive-table .empty-row {
    grid-column: 1 / -1;

    display: block !important;

    padding: 24px !important;
  }
}

@media (max-width: 420px) {
  .comprehensive-table tbody tr {
    padding: 10px 8px;
  }

  .comprehensive-table tbody td,
  .comprehensive-table tbody td:nth-child(n) {
    padding: 9px 5px;
  }

  .comprehensive-title-block h2 {
    font-size: 16px;
  }
}

/* ===== Compact comprehensive FIS table overrides ===== */

.comprehensive-card {
  margin-bottom: 20px;

  padding: 18px;

  border-radius: 14px;
}

.comprehensive-heading {
  gap: 16px;

  margin-bottom: 14px;
}

.comprehensive-title-block h2 {
  font-size: 20px;
}

.comprehensive-title-block p {
  margin-top: 5px;

  font-size: 12px;

  line-height: 1.7;
}

.facility-count-card {
  width: 112px;

  min-width: 112px;

  min-height: 62px;

  padding: 9px 12px;

  border-radius: 11px;
}

.facility-count-card strong {
  font-size: 22px;
}

.comprehensive-wrap {
  border-radius: 11px;
}

.comprehensive-table {
  min-width: 820px;
}

.comprehensive-table th,
.comprehensive-table td {
  padding: 11px 8px;
}

.comprehensive-table th {
  height: 76px;

  font-size: 11px;
}

.comprehensive-table .header-score {
  gap: 3px;
}

.comprehensive-table .header-score span {
  font-size: 12px;
}

.comprehensive-table .header-score::after {
  font-size: 9px;
}

.comprehensive-table .header-score small {
  min-width: 38px;

  min-height: 22px;

  padding: 2px 7px;

  border-radius: 7px;

  font-size: 9px;
}

.comprehensive-table tbody td {
  height: 72px;
}

.comprehensive-table .name {
  font-size: 12px;

  line-height: 1.55;
}

.fis-score-cell {
  gap: 4px;
}

.fis-score-cell strong {
  font-size: 16px;
}

.fis-score-cell small {
  font-size: 8px;
}

.fis-result-cell {
  font-size: 19px !important;
}

@media (max-width: 800px) {
  .comprehensive-card {
    padding: 13px 10px;
  }

  .comprehensive-heading {
    margin-bottom: 12px;
  }

  .facility-count-card {
    width: 100%;

    min-height: 50px;

    padding: 8px 12px;
  }

  .comprehensive-wrap {
    overflow: visible;

    border: 0;

    background: transparent;
  }

  .comprehensive-table {
    display: block;

    min-width: 0;
  }

  .comprehensive-table tbody {
    display: grid;

    gap: 10px;
  }

  .comprehensive-table tbody tr {
    display: grid;

    grid-template-columns: repeat(2, minmax(0, 1fr));

    gap: 0;

    padding: 12px;

    border: 1px solid #dbe3ee;

    border-inline-end: 4px solid #173f6b;

    border-radius: 13px;

    box-shadow: 0 5px 14px rgba(15, 23, 42, 0.05);
  }

  .comprehensive-table tbody td,
  .comprehensive-table tbody td:nth-child(n) {
    display: flex;

    min-height: 58px;

    height: auto;

    flex-direction: column;

    align-items: center;

    justify-content: center;

    gap: 4px;

    padding: 8px 5px;

    border: 0;

    border-bottom: 1px dashed #e2e8f0;

    text-align: center;
  }

  .comprehensive-table tbody td::before {
    display: block;

    content: attr(data-label);

    color: #64748b;

    font-size: 9px;

    font-weight: 800;

    text-align: center;
  }

  .comprehensive-table tbody td.rank {
    position: static;

    display: none;
  }

  .comprehensive-table tbody td.name {
    grid-column: 1 / -1;

    display: block;

    min-height: auto;

    padding: 4px 4px 10px;

    border-bottom: 1px solid #e2e8f0;

    font-size: 14px;

    text-align: center;
  }

  .comprehensive-table tbody td.name::before {
    display: none;
  }

  .comprehensive-table .fis-result-cell {
    grid-column: 1 / -1;

    min-height: 54px;

    border-bottom: 0;

    color: #0f9f3f;

    font-size: 20px !important;
  }

  .comprehensive-table .fis-result-cell::before {
    display: block;
  }

  .fis-score-cell strong {
    font-size: 15px;
  }

  .fis-score-cell small {
    font-size: 8px;

    white-space: normal;
  }
}

/* ================= Full-width FIS table without scroll ================= */
.comprehensive-wrap {
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
}

.comprehensive-table {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  table-layout: fixed;
}

.comprehensive-table th,
.comprehensive-table td {
  min-width: 0;
  padding: 14px 4px;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: normal;
}

.comprehensive-table .fis-rank-column {
  width: 3% !important;
}

.comprehensive-table .fis-facility-column {
  width: 16% !important;
}

.comprehensive-table th:nth-child(n + 3):nth-child(-n + 7) {
  width: 7% !important;
}

.comprehensive-table .fis-result-header,
.comprehensive-table .fis-result-cell {
  width: 7% !important;
}

.comprehensive-table .comprehensive-priority-header,
.comprehensive-table .comprehensive-priority-cell {
  width: 8% !important;
}

.comprehensive-table .comprehensive-timeframe-header,
.comprehensive-table .comprehensive-timeframe-cell {
  width: 10% !important;
}

.comprehensive-table .comprehensive-response-header,
.comprehensive-table .comprehensive-response-cell {
  width: 21% !important;
}

.comprehensive-table th {
  font-size: 11px;
  line-height: 1.5;
}

.comprehensive-table td {
  font-size: 11px;
  line-height: 1.6;
}

.comprehensive-table .name {
  font-size: 12px;
}

.comprehensive-table .fis-score-cell strong {
  font-size: 18px;
}

.comprehensive-table .fis-score-cell small {
  font-size: 8px;
  white-space: normal;
}

.comprehensive-table .fis-result-cell {
  font-size: 20px !important;
}

.comprehensive-priority-cell,
.comprehensive-timeframe-cell {
  text-align: center;
}

.comprehensive-response-cell {
  color: #334155;
  line-height: 1.9;
  text-align: start;
  overflow-wrap: anywhere;
}

@media (max-width: 800px) {
  .comprehensive-table {
    min-width: 0;
  }

  .comprehensive-table .fis-result-cell {
    border-bottom: 1px dashed #e2e8f0;
  }

  .comprehensive-table .comprehensive-priority-cell,
  .comprehensive-table .comprehensive-timeframe-cell,
  .comprehensive-table .comprehensive-response-cell {
    display: flex;
    width: 100% !important;
    min-height: 58px;
    height: auto;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 8px 5px;
    border: 0;
    border-bottom: 1px dashed #e2e8f0;
    text-align: center;
  }

  .comprehensive-table .comprehensive-priority-cell::before,
  .comprehensive-table .comprehensive-timeframe-cell::before,
  .comprehensive-table .comprehensive-response-cell::before {
    display: block;
    content: attr(data-label);
    color: #64748b;
    font-size: 9px;
    font-weight: 800;
    text-align: center;
  }

  .comprehensive-table .comprehensive-response-cell {
    grid-column: 1 / -1;
    border-bottom: 0;
    line-height: 1.8;
  }
}
</style>
